// external
import type { FC } from 'react';
import { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';

// interstate components
import { Box } from '@interstate/components/Box';
import { PlusSmallIcon } from '@interstate/components/Icons/PlusSmallIcon';
import { Typography } from '@interstate/components/Typography';

// store
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';

// selectors
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// constants
import { DEALER_DISCOUNTS } from '../constants';

// internal components and custom hook
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';
import { DiscountRow } from './DiscountRow.interstate';
import { useDealerDiscounts } from './useDealerDiscounts';

// custom CSS styles
import { dealerDiscountsSlideOutColors, dealerDiscountsSlideOutStyles } from './Discount.style';

// types
import type { DealerDiscount, DealerDiscountSlideOutProps, DiscountOption } from './discountTypes';
import type { ItemizedDiscount } from '@makemydeal/dr-platform-types';

// utils
import {
    isSingleEmptyRowUtil,
    getNonEmptyDiscountsUtil,
    mapDealerDiscountValuesToOptions,
    mapToTemporaryDealerDiscounts,
    isValidName,
    isValidAmount
} from '../utils/discountUtils';
import { hasValidationErrors } from './validation';
import { SlideOutWithFixedFooter } from '../utils/SlideOutWithFixedFooter';
import { getReturnToOfferButton } from '@makemydeal/dr-shared-ui-utils';

const DealerDiscountsSlideOut: FC<DealerDiscountSlideOutProps> = ({ show, onHide }) => {
    const dispatch = useDispatch();
    const dealerDiscountValues = useSelector(dealerSelectors.getAdminDealerDiscounts);
    const penciledInputsDiscounts = useSelector(offerSelectors.getPenciledInputDiscounts);

    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [originalDiscounts, setOriginalDiscounts] = useState<DealerDiscount[]>([]);

    const drAdminDealerDiscountCodes: DiscountOption[] = useMemo(
        () => mapDealerDiscountValuesToOptions(dealerDiscountValues),
        [dealerDiscountValues]
    );

    const pencilInputDiscounts: DealerDiscount[] = useMemo(
        () => mapToTemporaryDealerDiscounts(penciledInputsDiscounts),
        [penciledInputsDiscounts]
    );

    const {
        discounts,
        getAvailableOptions,
        getSelectedValue,
        addDiscount,
        removeDiscount,
        updateDiscountName,
        updateDiscountAmount,
        clearDiscount
    } = useDealerDiscounts(drAdminDealerDiscountCodes, pencilInputDiscounts);

    const nonEmptyDiscounts = getNonEmptyDiscountsUtil(discounts);
    const hasErrors = hasValidationErrors(nonEmptyDiscounts);

    // Store original discounts when slideout opens (only when show becomes true)
    useEffect(() => {
        if (show) {
            setOriginalDiscounts(structuredClone(discounts));
        }
    }, [show]); // Only depend on 'show', not 'discounts'

    const hasUnsavedChanges = (discountsToCompareWithOriginal: DealerDiscount[]) => {
        if (isSingleEmptyRowUtil(originalDiscounts) && discountsToCompareWithOriginal.length == 0) {
            return false;
        }
        return !isEqual(originalDiscounts, discountsToCompareWithOriginal);
    };

    const applyDiscountsAndClose = () => {
        const validDiscounts = discounts
            .filter((discount) => isValidName(discount.discountName) && isValidAmount(discount.discountAmount))
            .map(({ amountError: _amountError, nameError: _nameError, ...rest }) => rest);

        const dealerDiscounts: ItemizedDiscount[] = validDiscounts.map((discount) => ({
            discountName: discount.discountName,
            discountAmount:
                typeof discount.discountAmount === 'number'
                    ? discount.discountAmount
                    : Number.parseFloat(discount.discountAmount) || 0
        }));

        if (hasUnsavedChanges(validDiscounts)) {
            dispatch(offerReduxActionCreators.applyDealerDiscounts(dealerDiscounts));
        }
        dispatch(onHide);
    };

    const handleDiscardChanges = () => {
        setShowDiscardModal(false);
        dispatch(onHide);
    };

    const handleGoBack = () => {
        setShowDiscardModal(false);
    };

    const handleApplyChanges = () => {
        if (hasErrors) {
            setShowDiscardModal(true);
            return;
        }
        applyDiscountsAndClose();
    };

    return (
        <>
            <DiscardChangesModal
                show={showDiscardModal}
                onDiscard={handleDiscardChanges}
                onGoBack={handleGoBack}
                hasValidationErrors={hasErrors}
            />
            <SlideOutWithFixedFooter
                show={show}
                onHide={handleApplyChanges}
                header={DEALER_DISCOUNTS}
                position="right"
                data-testid="manual-discount-slideout"
                panelWidth="50%"
                footer={getReturnToOfferButton(handleApplyChanges)}
            >
                <Box style={dealerDiscountsSlideOutStyles.singleRowContainer}>
                    {/* MAIN discount component row with:
                1. Discount code dropdown (called "ComboBox" by interstate library),
                2. Numeric input box for amount,
                3. Delete button represented by interstate's trash icon */}
                    {discounts.map((discount) => (
                        <DiscountRow
                            key={discount.id}
                            discount={discount}
                            availableOptions={getAvailableOptions()}
                            selectedValue={getSelectedValue(discount.discountName, discount.discountAmount)}
                            canRemove={discounts.length > 1}
                            onNameChange={(selectedOption) => updateDiscountName(discount.id, selectedOption)}
                            onAmountChange={(value) => updateDiscountAmount(discount.id, value)}
                            onRemove={() => removeDiscount(discount.id)}
                            onClear={() => clearDiscount(discount.id)}
                            nameError={discount.nameError}
                            amountError={discount.amountError}
                        />
                    ))}

                    {/* This component is the "Add Discount Button",
                which adds another row of above component from DiscountRow.interstate.tsx,
                so we can have multiple rows of discounts */}
                    <Box component="button" style={dealerDiscountsSlideOutStyles.addDiscountButton} onClick={addDiscount}>
                        <PlusSmallIcon color={dealerDiscountsSlideOutColors.primaryColor} />
                        <Typography
                            variant="body-md"
                            color={dealerDiscountsSlideOutColors.primaryColor}
                            sx={dealerDiscountsSlideOutStyles.addDiscountText}
                        >
                            &nbsp;Add Discount
                        </Typography>
                    </Box>
                </Box>
            </SlideOutWithFixedFooter>
        </>
    );
};

export default DealerDiscountsSlideOut;
